import React, { useEffect } from "react";
import BaseList from "../../../../components/partials/common/base-list";
import { useTranslation } from "react-i18next";
import { getPathParam } from "../../../../utils/converter";
import {
  COMPANY,
  COMPANY_PAYMENT_TERM,
  COMPANY_PAYMENT_TERMS
} from "../../../../api/endpoints";
import { useSelector } from "react-redux";
import ActionsMenu from "../../../../components/common/table/actions-menu";
import CardDetail from "../../../../components/common/list/card-detail";
import TableDetail from "../../../../components/common/list/table-detail";
import { Controller, useForm } from "react-hook-form";
import InputText from "../../../../components/common/form/input-text";
import { Warning } from "../../../../components/common/alert/banner";
import InputSubmit from "../../../../components/common/form/input-submit";
import PaymentTermsConditions from "./payment-terms-conditions";
import { CreditCardIcon } from "@heroicons/react/outline";
import BasePermissionWrapper from "../../../../components/partials/restricted/base-permission-wrapper";
import {
  getPermission,
  permissionGroup,
  permissionSpecific
} from "../../../../constants/permissions";

const defaultUnit = "after_invoice_date";

const normalizeConditions = (conditions) => {
  return (conditions ?? []).map((condition, index) => ({
    _key: condition?._key ?? condition?.id ?? `row-${index + 1}`,
    id: condition?.id ?? null,
    percent: typeof condition?.percent === "number" ? condition.percent : Number(condition?.percent ?? 0),
    days: typeof condition?.days === "number" ? condition.days : Number(condition?.days ?? 0),
    unit: condition?.unit ?? defaultUnit
  }));
};

const sanitizeConditions = (conditions) => {
  return (conditions ?? [])
    .map((condition) => ({
      id: condition?.id ?? null,
      percent: Number(condition?.percent ?? 0),
      days: Number(condition?.days ?? 0),
      unit: condition?.unit ?? defaultUnit
    }))
    .filter((condition) => Number.isFinite(condition.percent) && Number.isFinite(condition.days))
    .map((condition) => {
      const payload = {
        percent: condition.percent,
        days: condition.days,
        unit: condition.unit
      };
      if (condition.id) {
        payload.id = condition.id;
      }
      return payload;
    });
};

const formatConditionUnit = (unit, t) => {
  switch (unit) {
    case "after_end_of_month":
      return t("company.form.due_terms_unit_after_end_of_month", { defaultValue: "after end of month" });
    case "after_end_of_next_month":
      return t("company.form.due_terms_unit_after_end_of_next_month", { defaultValue: "after end of next month" });
    case "after_invoice_date":
    default:
      return t("company.form.due_terms_unit_after_invoice_date", { defaultValue: "after invoice date" });
  }
};

const formatConditionsLabel = (conditions, t) => {
  if (!Array.isArray(conditions) || conditions.length === 0) {
    return "-";
  }

  return conditions
    .map((condition) => {
      const percent = Number(condition?.percent ?? 0);
      const days = Number(condition?.days ?? 0);
      const unitLabel = formatConditionUnit(condition?.unit, t);
      return `${percent}% - ${days} ${unitLabel}`;
    })
    .join("\n");
};

const PaymentTerms = () => {
  const { t } = useTranslation();
  const { loggedCompany } = useSelector((state) => state.user);

  return (
    <BasePermissionWrapper
      requiredPermissions={[
        getPermission(
          permissionGroup.COMPANY_CONFIGURATION,
          permissionSpecific.COMPANY_CONF_PAYMENT_TERMS
        )
      ]}
      renderBaseOnFail={true}
    >
      {loggedCompany && (
        <BaseList
          columns={[
            t("app.payment_terms.name", { defaultValue: "Name" }),
            t("app.payment_terms.code", { defaultValue: "Code" }),
            t("app.payment_terms.conditions", { defaultValue: "Conditions" }),
            ""
          ]}
          endpoint={getPathParam([COMPANY, loggedCompany.id, COMPANY_PAYMENT_TERMS])}
          storeEndpoint={getPathParam([COMPANY, loggedCompany.id, COMPANY_PAYMENT_TERM])}
          updateEndpoint={getPathParam([
            COMPANY,
            loggedCompany.id,
            COMPANY_PAYMENT_TERM,
            ":id"
          ])}
          baseResult={{ total_rows: 0, payment_terms: [] }}
          resultLabel={"payment_terms"}
          title={t("app.payment_terms.title", { defaultValue: "Payment Terms" })}
          showHeader={true}
          mobRow={MobRow}
          deskRow={DeskRow}
          addForm={AddPaymentTerm}
          editForm={EditPaymentTerm}
          icon={CreditCardIcon}
          addFormLabel={t("app.payment_terms.add", { defaultValue: "Add payment term" })}
          btnAddLabel={t("app.payment_terms.add", { defaultValue: "Add payment term" })}
          editTitle={t("app.payment_terms.edit_title", { defaultValue: "Edit payment term" })}
          isActiveEdit={true}
        />
      )}
    </BasePermissionWrapper>
  );
};

const MobRow = ({ element, extras }) => {
  const { t } = useTranslation();
  const { loggedCompany } = useSelector((state) => state.user);

  const removeDetail = {
    title: t("app.payment_terms.remove_title", { defaultValue: "Remove payment term" }),
    message: t("app.payment_terms.remove_message", { defaultValue: "Are you sure you want to delete this payment term?" }),
    endpoint: getPathParam([COMPANY, loggedCompany.id, COMPANY_PAYMENT_TERM, element.id])
  };

  return (
    <li key={element.id} className="flex items-center gap-4 p-4">
      <div className="flex-grow">
        <div className="flex items-center justify-between pb-4">
          <div>
            <div className="text-sm font-semibold text-gray-900">{element.name}</div>
            <div className="text-xs text-gray-500 uppercase">{element.code}</div>
          </div>
          <ActionsMenu>
            <button
              className="text-left"
              onClick={() => {
                extras.onEdit(true, element);
              }}
            >
              {t("app.common.edit", { defaultValue: "Edit" })}
            </button>
            <button
              className="text-left"
              onClick={() => {
                extras.onRemove(true, removeDetail);
              }}
            >
              {t("app.common.remove", { defaultValue: "Remove" })}
            </button>
          </ActionsMenu>
        </div>
        <div className="flex flex-col">
          <CardDetail title={t("app.payment_terms.conditions", { defaultValue: "Conditions" })}>
            <pre className="whitespace-pre-wrap text-sm text-gray-700">
              {formatConditionsLabel(element.condition_type, t)}
            </pre>
          </CardDetail>
          <CardDetail title={t("app.common.description", { defaultValue: "Description" })}>
            {element.description ?? "-"}
          </CardDetail>
        </div>
      </div>
    </li>
  );
};

const DeskRow = ({ element, extras }) => {
  const { t } = useTranslation();
  const { loggedCompany } = useSelector((state) => state.user);

  const removeDetail = {
    title: t("app.payment_terms.remove_title", { defaultValue: "Remove payment term" }),
    message: t("app.payment_terms.remove_message", { defaultValue: "Are you sure you want to delete this payment term?" }),
    endpoint: getPathParam([COMPANY, loggedCompany.id, COMPANY_PAYMENT_TERM, element.id])
  };

  return (
    <tr key={element.id}>
      <TableDetail extraClass="max-w-sm">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-900">{element.name}</span>
          <span className="text-xs text-gray-500 uppercase">{element.code}</span>
        </div>
      </TableDetail>

      <TableDetail>
        <pre className="whitespace-pre-wrap text-sm text-gray-700">
          {formatConditionsLabel(element.condition_type, t)}
        </pre>
      </TableDetail>

      <TableDetail>
        <ActionsMenu>
          <button
            className="text-left"
            onClick={() => {
              extras.onEdit(true, element);
            }}
          >
            {t("app.common.edit", { defaultValue: "Edit" })}
          </button>
          <button
            className="text-left"
            onClick={() => {
              extras.onRemove(true, removeDetail);
            }}
          >
            {t("app.common.remove", { defaultValue: "Remove" })}
          </button>
        </ActionsMenu>
      </TableDetail>
    </tr>
  );
};

const PaymentTermFormFields = ({ control, register, errors }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <InputText
        id="payment_term_name"
        type="text"
        label={t("app.payment_terms.name", { defaultValue: "Name" })}
        errors={errors.name}
        isRequired={true}
        input={{
          ...register("name", {
            required: true,
            validate: (value) => (value ?? "").trim().length > 0
          })
        }}
      />

      <InputText
        id="payment_term_code"
        type="text"
        label={t("app.payment_terms.code", { defaultValue: "Code" })}
        errors={errors.code}
        isRequired={true}
        input={{
          ...register("code", {
            required: true,
            validate: (value) => (value ?? "").trim().length > 0
          })
        }}
      />

      <Controller
        control={control}
        name="condition_type"
        rules={{ validate: (value) => Array.isArray(value) && value.length > 0 }}
        render={({ field: { value, onChange } }) => (
          <PaymentTermsConditions
            baseValue={normalizeConditions(value)}
            onChange={(rows) => onChange(rows)}
          />
        )}
      />

      <InputText
        id="payment_term_description"
        type="textarea-2"
        label={t("app.common.description", { defaultValue: "Description" })}
        errors={errors.description}
        input={{ ...register("description") }}
      />
    </div>
  );
};

const AddPaymentTerm = ({ exception, isLoading, onSubmitData }) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: "",
      code: "",
      description: "",
      condition_type: normalizeConditions([
        { id: 1, percent: 100, days: 0, unit: defaultUnit }
      ])
    }
  });

  const onSubmit = (data) => {
    const payload = {
      ...data,
      name: data.name?.trim(),
      code: data.code?.trim(),
      description: data.description?.trim() || null,
      condition_type: sanitizeConditions(data.condition_type)
    };
    onSubmitData(payload);
  };

  const { t } = useTranslation();

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PaymentTermFormFields control={control} register={register} errors={errors} />

      {exception && <Warning message={exception} />}

      <div className="mt-3">
        <InputSubmit
          isLoading={isLoading}
          isFullWith={true}
          label={t("app.form.save", { defaultValue: "Save" })}
        />
      </div>
    </form>
  );
};

const EditPaymentTerm = ({ data, exception, isEditLoading, onSubmitData }) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      id: data?.id ?? "",
      name: data?.name ?? "",
      code: data?.code ?? "",
      description: data?.description ?? "",
      condition_type: normalizeConditions(data?.condition_type)
    }
  });

  useEffect(() => {
    reset({
      id: data?.id ?? "",
      name: data?.name ?? "",
      code: data?.code ?? "",
      description: data?.description ?? "",
      condition_type: normalizeConditions(data?.condition_type)
    });
  }, [data, reset]);

  const onSubmit = (formData) => {
    const payload = {
      ...formData,
      id: formData?.id ?? data?.id ?? "",
      name: formData.name?.trim(),
      code: formData.code?.trim(),
      description: formData.description?.trim() || null,
      condition_type: sanitizeConditions(formData.condition_type)
    };
    onSubmitData(payload);
  };

  const { t } = useTranslation();

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PaymentTermFormFields control={control} register={register} errors={errors} />

      {exception && <Warning message={exception} />}

      <div className="mt-3">
        <InputSubmit
          isLoading={isEditLoading}
          isFullWith={true}
          label={t("app.form.save", { defaultValue: "Save" })}
        />
      </div>
    </form>
  );
};

export default PaymentTerms;
